import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import { useOrganizationPolicies } from "@/hooks/useDNSPolicies";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield,
  AlertTriangle,
  Search,
  Filter,
  Eye,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  Globe,
  FileText,
  Users,
  Zap,
  Target,
  BarChart3,
  PieChart,
  LineChart,
  Download,
  RefreshCw,
  Info
} from "lucide-react";
import { Label } from "@/components/ui/label";

interface ThreatData {
  id: string;
  domain: string;
  threatType: 'malware' | 'phishing' | 'botnet' | 'spam' | 'adware' | 'suspicious';
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  firstSeen: string;
  lastSeen: string;
  blockCount: number;
  affectedUsers: number;
  status: 'active' | 'blocked' | 'investigating' | 'resolved';
  description: string;
  ioc?: string;
  tags: string[];
}

interface ThreatStats {
  totalThreats: number;
  activeThreats: number;
  blockedThreats: number;
  threatsToday: number;
  threatsThisWeek: number;
  threatsThisMonth: number;
  topThreatTypes: { type: string; count: number }[];
  topCategories: { category: string; count: number }[];
}

const mockThreatData: ThreatData[] = [
  {
    id: '1',
    domain: 'malware.example.com',
    threatType: 'malware',
    severity: 'critical',
    category: 'Malware Distribution',
    firstSeen: '2024-01-15T10:30:00Z',
    lastSeen: '2024-01-20T14:45:00Z',
    blockCount: 156,
    affectedUsers: 23,
    status: 'blocked',
    description: 'Known malware distribution site distributing trojans and ransomware',
    ioc: '192.168.1.100',
    tags: ['malware', 'ransomware', 'trojan', 'critical']
  },
  {
    id: '2',
    domain: 'phishing-site.net',
    threatType: 'phishing',
    severity: 'high',
    category: 'Credential Theft',
    firstSeen: '2024-01-18T09:15:00Z',
    lastSeen: '2024-01-20T16:20:00Z',
    blockCount: 89,
    affectedUsers: 12,
    status: 'active',
    description: 'Phishing site targeting banking credentials and personal information',
    ioc: '10.0.0.50',
    tags: ['phishing', 'credentials', 'banking', 'high']
  },
  {
    id: '3',
    domain: 'botnet-control.org',
    threatType: 'botnet',
    severity: 'high',
    category: 'Command & Control',
    firstSeen: '2024-01-10T11:00:00Z',
    lastSeen: '2024-01-20T13:30:00Z',
    blockCount: 234,
    affectedUsers: 45,
    status: 'investigating',
    description: 'Botnet command and control server for IoT device botnet',
    ioc: '172.16.0.100',
    tags: ['botnet', 'iot', 'c2', 'high']
  },
  {
    id: '4',
    domain: 'spam-gateway.com',
    threatType: 'spam',
    severity: 'medium',
    category: 'Spam Distribution',
    firstSeen: '2024-01-12T08:45:00Z',
    lastSeen: '2024-01-20T15:15:00Z',
    blockCount: 67,
    affectedUsers: 8,
    status: 'blocked',
    description: 'Bulk email spam distribution server',
    ioc: '203.0.113.50',
    tags: ['spam', 'email', 'bulk', 'medium']
  },
  {
    id: '5',
    domain: 'adware-network.biz',
    threatType: 'adware',
    severity: 'low',
    category: 'Adware Distribution',
    firstSeen: '2024-01-16T12:20:00Z',
    lastSeen: '2024-01-20T11:45:00Z',
    blockCount: 34,
    affectedUsers: 5,
    status: 'blocked',
    description: 'Adware distribution network serving unwanted advertisements',
    ioc: '198.51.100.75',
    tags: ['adware', 'ads', 'unwanted', 'low']
  }
];

const mockThreatStats: ThreatStats = {
  totalThreats: 156,
  activeThreats: 23,
  blockedThreats: 133,
  threatsToday: 8,
  threatsThisWeek: 34,
  threatsThisMonth: 156,
  topThreatTypes: [
    { type: 'Malware', count: 45 },
    { type: 'Phishing', count: 38 },
    { type: 'Botnet', count: 29 },
    { type: 'Spam', count: 24 },
    { type: 'Adware', count: 20 }
  ],
  topCategories: [
    { category: 'Malware Distribution', count: 52 },
    { category: 'Credential Theft', count: 41 },
    { category: 'Command & Control', count: 33 },
    { category: 'Spam Distribution', count: 30 }
  ]
};

export default function Threats() {
  const { data: organization } = useUserOrganization();
  const { data: policies = [] } = useOrganizationPolicies();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedThreatType, setSelectedThreatType] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [timeRange, setTimeRange] = useState('7d');

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to view threat intelligence.
          </p>
        </div>
      </div>
    );
  }

  const filteredThreats = mockThreatData.filter(threat => {
    const matchesSearch = threat.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         threat.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         threat.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesThreatType = selectedThreatType === 'all' || threat.threatType === selectedThreatType;
    const matchesSeverity = selectedSeverity === 'all' || threat.severity === selectedSeverity;
    const matchesStatus = selectedStatus === 'all' || threat.status === selectedStatus;

    return matchesSearch && matchesThreatType && matchesSeverity && matchesStatus;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'blocked': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'investigating': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getThreatTypeIcon = (threatType: string) => {
    switch (threatType) {
      case 'malware': return <AlertTriangle className="h-4 w-4" />;
      case 'phishing': return <Target className="h-4 w-4" />;
      case 'botnet': return <Users className="h-4 w-4" />;
      case 'spam': return <FileText className="h-4 w-4" />;
      case 'adware': return <Globe className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const exportThreatReport = () => {
    // TODO: Implement threat report export
    console.log('Exporting threat report...');
  };

  const refreshThreatData = () => {
    // TODO: Implement threat data refresh
    console.log('Refreshing threat data...');
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SiteHeader />
      <SidebarInset>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Threat Intelligence</h1>
              <p className="text-muted-foreground">
                Monitor and analyze security threats for {organization.name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-40">
                  <Clock className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={refreshThreatData} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={exportThreatReport}>
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Threat Statistics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Threats</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{mockThreatStats.totalThreats}</div>
                <p className="text-xs text-muted-foreground">
                  <TrendingUp className="inline h-3 w-3 mr-1" />
                  +12% from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Threats</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {mockThreatStats.activeThreats}
                </div>
                <p className="text-xs text-muted-foreground">
                  <TrendingDown className="inline h-3 w-3 mr-1" />
                  -5% from last week
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Blocked Threats</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {mockThreatStats.blockedThreats}
                </div>
                <p className="text-xs text-muted-foreground">
                  <TrendingUp className="inline h-3 w-3 mr-1" />
                  +8% success rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Threats Today</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {mockThreatStats.threatsToday}
                </div>
                <p className="text-xs text-muted-foreground">
                  <TrendingDown className="inline h-3 w-3 mr-1" />
                  -3 from yesterday
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="threats">Active Threats</TabsTrigger>
              <TabsTrigger value="analysis">Threat Analysis</TabsTrigger>
              <TabsTrigger value="ioc">IOC Database</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Threat Types Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Threat Types Distribution
                    </CardTitle>
                    <CardDescription>
                      Breakdown of threats by type over {timeRange}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {mockThreatStats.topThreatTypes.map((threatType, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              index === 0 ? 'bg-red-500' :
                              index === 1 ? 'bg-orange-500' :
                              index === 2 ? 'bg-yellow-500' :
                              index === 3 ? 'bg-blue-500' : 'bg-gray-500'
                            }`} />
                            <span className="text-sm">{threatType.type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  index === 0 ? 'bg-red-500' :
                                  index === 1 ? 'bg-orange-500' :
                                  index === 2 ? 'bg-yellow-500' :
                                  index === 3 ? 'bg-blue-500' : 'bg-gray-500'
                                }`}
                                style={{ width: `${(threatType.count / mockThreatStats.totalThreats) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8">{threatType.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Threat Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Threat Categories
                    </CardTitle>
                    <CardDescription>
                      Most common threat categories
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {mockThreatStats.topCategories.map((category, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm">{category.category}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${(category.count / mockThreatStats.totalThreats) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8">{category.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Recent Threat Activity
                  </CardTitle>
                  <CardDescription>
                    Latest threat detections and responses
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {mockThreatData.slice(0, 5).map((threat) => (
                      <div key={threat.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          {getThreatTypeIcon(threat.threatType)}
                          <div>
                            <p className="font-medium text-sm">{threat.domain}</p>
                            <p className="text-xs text-muted-foreground">
                              {threat.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(threat.severity)}>
                            {threat.severity}
                          </Badge>
                          <Badge variant="outline" className={getStatusColor(threat.status)}>
                            {threat.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(threat.lastSeen).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Active Threats Tab */}
            <TabsContent value="threats" className="space-y-6">
              {/* Search and Filters */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search threats..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <div className="flex gap-2">
                  <Select value={selectedThreatType} onValueChange={setSelectedThreatType}>
                    <SelectTrigger className="w-40">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Threat Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="malware">Malware</SelectItem>
                      <SelectItem value="phishing">Phishing</SelectItem>
                      <SelectItem value="botnet">Botnet</SelectItem>
                      <SelectItem value="spam">Spam</SelectItem>
                      <SelectItem value="adware">Adware</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                    <SelectTrigger className="w-32">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-32">
                      <Eye className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Threats List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Threats ({filteredThreats.length})</h3>
                  <Badge variant="outline">
                    {filteredThreats.filter(t => t.status === 'active').length} Active
                  </Badge>
                </div>

                {filteredThreats.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-medium mb-2">No threats found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your search criteria
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {filteredThreats.map((threat) => (
                      <Card key={threat.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              {getThreatTypeIcon(threat.threatType)}
                              <div>
                                <CardTitle className="text-lg">{threat.domain}</CardTitle>
                                <CardDescription>{threat.description}</CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={getSeverityColor(threat.severity)}>
                                {threat.severity}
                              </Badge>
                              <Badge variant="outline" className={getStatusColor(threat.status)}>
                                {threat.status}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div>
                              <Label className="text-sm font-medium">Category</Label>
                              <p className="text-sm text-muted-foreground">{threat.category}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">First Seen</Label>
                              <p className="text-sm text-muted-foreground">
                                {new Date(threat.firstSeen).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Block Count</Label>
                              <p className="text-sm text-muted-foreground">{threat.blockCount}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Affected Users</Label>
                              <p className="text-sm text-muted-foreground">{threat.affectedUsers}</p>
                            </div>
                          </div>

                          {threat.ioc && (
                            <div className="mt-4 p-3 bg-muted rounded-lg">
                              <Label className="text-sm font-medium">IOC (Indicator of Compromise)</Label>
                              <p className="font-mono text-sm mt-1">{threat.ioc}</p>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1 mt-4">
                            {threat.tags.map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex gap-2 mt-4">
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                            <Button size="sm" variant="outline">
                              <Shield className="h-4 w-4 mr-2" />
                              Block Domain
                            </Button>
                            <Button size="sm" variant="outline">
                              <Info className="h-4 w-4 mr-2" />
                              Report
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Threat Analysis Tab */}
            <TabsContent value="analysis" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Threat Trends */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <LineChart className="h-5 w-5" />
                      Threat Trends
                    </CardTitle>
                    <CardDescription>
                      Threat activity over time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">This Week</span>
                        <Badge variant="outline">{mockThreatStats.threatsThisWeek}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">This Month</span>
                        <Badge variant="outline">{mockThreatStats.threatsThisMonth}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Success Rate</span>
                        <Badge variant="default">
                          {Math.round((mockThreatStats.blockedThreats / mockThreatStats.totalThreats) * 100)}%
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Policy Effectiveness */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-4" />
                      Policy Effectiveness
                    </CardTitle>
                    <CardDescription>
                      How well your policies are blocking threats
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Active Policies</span>
                        <Badge variant="default">
                          {policies.filter((p: any) => p.is_active).length}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Threats Blocked</span>
                        <Badge variant="outline">{mockThreatStats.blockedThreats}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Response Time</span>
                        <Badge variant="secondary">~2.3s</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* IOC Database Tab */}
            <TabsContent value="ioc" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Indicators of Compromise (IOC)
                  </CardTitle>
                  <CardDescription>
                    Database of known malicious indicators
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Total IOCs</span>
                      <Badge variant="outline">
                        {mockThreatData.filter(t => t.ioc).length}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      {mockThreatData.filter(t => t.ioc).map((threat) => (
                        <div key={threat.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{threat.ioc}</p>
                            <p className="text-xs text-muted-foreground">{threat.domain}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getSeverityColor(threat.severity)}>
                              {threat.severity}
                            </Badge>
                            <Badge variant="outline">
                              {threat.threatType}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
